import {
  DeleteOutlined,
  FileOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, message } from "antd";
import { useRef, useState } from "react";
import "./claimInvestigator.scss";
import ArrowUpIcon from "../../assets/images/icons/arrow_up.svg";

// ─────────────────────────────────────────────────────────────
// Constants & helpers
// ─────────────────────────────────────────────────────────────
export const MAX_FILES = 50;
export const MAX_MB = 100;

export const trimFileName = (name = "", max = 100) =>
  name.length > max ? name.slice(0, max - 1) + "…" : name;

export const groupLabel = (idx) => `Group ${idx + 1}`;

export const buildGroups = (files = []) => {
  if (!files.length) return [];
  return [{
    groupName: groupLabel(0),
    files: files,
  }];
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────
/**
 * Props
 *   fileList        – File[]
 *   handleFileSelect– (File[]) => void
 *   onSubmit        – () => void
 *   disabled        – boolean
 *   fileInputRef    – React ref (optional, passed in from parent)
 *   maxFiles        – number (default MAX_FILES)
 */
const UploadMedia = ({
  fileList = [],
  handleFileSelect,
  onSubmit,
  disabled,
  fileInputRef: externalRef,
  maxFiles = MAX_FILES,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const internalRef = useRef(null);
  const inputRef = externalRef ?? internalRef;

  // ── Validation ─────────────────────────────────────────────
  const validate = (file) => {
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      message.destroy();
      message.error(`"${file.name}" is not a PDF file.`);
      return false;
    }
    if (file.size / 1024 / 1024 >= MAX_MB) {
      message.error(`"${file.name}" exceeds ${MAX_MB} MB.`);
      return false;
    }
    return true;
  };

const addFiles = (incoming) => {
  const valid = Array.from(incoming).filter(validate);
  if (!valid.length) return;

  // ── Duplicate check ──────────────────────────────────────
  const duplicates = [];
  const unique = valid.filter((newFile) => {
    const isDuplicate = fileList.some(
      (existing) =>
        (existing instanceof File ? existing : existing.originFileObj)?.name === newFile.name &&
        (existing instanceof File ? existing : existing.originFileObj)?.size === newFile.size
    );
    if (isDuplicate) duplicates.push(newFile.name);
    return !isDuplicate;
  });

  if (duplicates.length) {
    message.destroy();
    message.warning(
      `${duplicates.length === 1
        ? `"${trimFileName(duplicates[0], 40)}" is already added.`
        : `${duplicates.length} files are already added.`
      }`
    );
  }

  if (!unique.length) return;
  // ─────────────────────────────────────────────────────────

  const merged = [...fileList, ...unique];
  if (merged.length > maxFiles) {
    message.destroy();
    message.warning(`Maximum ${maxFiles} files allowed. Extra files were ignored.`);
    handleFileSelect(merged.slice(0, maxFiles));
    return;
  }
  handleFileSelect(merged);
};

  const removeFile = (globalIdx, e) => {
    e.stopPropagation();
    handleFileSelect(fileList.filter((_, i) => i !== globalIdx));
  };

  const groups = buildGroups(fileList);

  const zoneClass = [
    "ci-upload__zone",
    isDragging ? "ci-upload__zone--dragging" : "",
    fileList.length > 0 ? "ci-upload__zone--has-files" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div style={{ background: "white", borderRadius: "14px" }}>
        <div className="ci-upload">

          {/* ── Drop zone ── */}
          <div
            className={zoneClass}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragEnter={() => setIsDragging(true)}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              addFiles(e.dataTransfer.files);
            }}
            onClick={() => inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.PDF"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />

            {/* ── Empty state: centred prompt ── */}
            {fileList.length === 0 && (
              <div className="ci-upload__empty">
                <p className="ci-upload__empty-text">Drag and drop here or</p>
                <button className="btn-outlined">
                    <div className='btn-outlined-content'>
                      <div className='text'>Upload documents </div>
                      <img width={29} src={ArrowUpIcon} />
                    </div>
                  </button>
              </div>
            )}

            {/* ── File list — slides left when files are added ── */}
            {fileList.length > 0 && (
              <div
                className="ci-upload__file-area"
              // onClick={(e) => e.stopPropagation()}
              >
                {/* Left: file cards */}
                <div className="ci-upload__file-list">
                  {groups.map((group, gi) => (
                    <div key={group.groupName} className="ci-upload__group-section">
                      <div className="ci-upload__group-section__header">
                        <span className="ci-upload__group-section__header-count">
                          {group.files.length} file{group.files.length !== 1 ? "s" : ""}
                        </span>
                      </div>

                      <div className="ci-upload__file-cards">
                        {group.files.map((f, fi) => {
                          const globalIdx = gi * maxFiles + fi;
                          const file = f instanceof File ? f : f.originFileObj;
                          const name = file?.name ?? "unknown";
                          const mb = ((file?.size ?? 0) / 1024 / 1024).toFixed(1);
                          return (
                            <div key={globalIdx} className="ci-upload__file-card">
                              <div className="ci-upload__file-card__top">
                                <span className="ci-upload__file-card__badge">PDF</span>
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  className="ci-upload__file-card__del"
                                  onClick={(e) => removeFile(globalIdx, e)}
                                />
                              </div>
                              <FileOutlined className="ci-upload__file-card__icon" />
                              <span className="ci-upload__file-card__name" title={name}>
                                {trimFileName(name, 28)}
                              </span>
                              <span className="ci-upload__file-card__size">{mb} MB</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Right: add more prompt */}
                <div className="ci-upload__add-more">
                  <p className="ci-upload__empty-text">Drag and drop here or</p>
                  <button className="btn-outlined">
                    <div className='btn-outlined-content'>
                      <div className='text'>Upload documents </div>
                      <img width={29} src={ArrowUpIcon} />
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Footer: Submit button right-aligned ── */}
          <div className="ci-upload__footer">
            <Button
              type="primary"
              onClick={onSubmit}
              disabled={disabled || fileList.length === 0}
              loading={disabled}
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UploadMedia;